import { useState } from "react";
import { comments } from "@/mocks/homeContent";
import { useReveal } from "@/hooks/useReveal";

export default function CommentBuzz() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const { ref, visible } = useReveal<HTMLDivElement>(0.1);

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="buzz" className="w-full bg-background-100 py-14 md:py-20">
      <div
        ref={ref}
        className={`mx-auto w-full px-4 md:px-8 ${visible ? "reveal is-visible" : "reveal"}`}
      >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              <span className="text-xs font-medium tracking-[0.18em] text-secondary-600">
                COMMUNITY BUZZ
              </span>
            </div>
            <h2 className="font-heading mt-3 text-2xl font-bold text-foreground-950 md:text-4xl">
              正在热议
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              话题墙下的讨论同样精彩。会员常在这里补充线索、纠正时间顺序，很多脉络就是这样被一起拼完整的。
            </p>

            <div className="mt-7 space-y-4">
              {comments.map((item) => {
                const isLiked = Boolean(liked[item.id]);
                return (
                  <article
                    key={item.id}
                    className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-200 font-heading text-base font-semibold text-secondary-900">
                        {item.initial}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-foreground-900">{item.user}</span>
                          {item.badge && (
                            <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">
                              <i className="ri-vip-crown-2-line" />
                              {item.badge}
                            </span>
                          )}
                          <span className="text-xs text-foreground-400">{item.time}</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                          {item.content}
                        </p>
                        <div className="mt-2 flex items-center gap-1 text-xs text-secondary-600">
                          <i className="ri-link" />
                          来自话题：{item.topic}
                        </div>
                        <div className="mt-3 flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => toggleLike(item.id)}
                            className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                              isLiked
                                ? "border-primary-300 bg-primary-100 text-primary-700"
                                : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                            }`}
                          >
                            <i className={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                            {item.likes + (isLiked ? 1 : 0)}
                          </button>
                          <button
                            type="button"
                            className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-secondary-400"
                          >
                            <i className="ri-chat-3-line" />
                            {item.replies} 条回复
                          </button>
                          <button
                            type="button"
                            className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-secondary-400"
                          >
                            <i className="ri-share-forward-line" />
                            分享
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-24 rounded-lg border border-background-300 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">参与讨论</h3>
              <p className="mt-2 text-sm text-foreground-600">
                说说你关注的话题，或者补充一条你注意到的线索。
              </p>
              <div className="mt-4">
                <textarea
                  rows={4}
                  maxLength={500}
                  placeholder="写下你的看法或补充线索…"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["#剧组幕后", "#时间线", "#合约解读"].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800 transition-colors hover:bg-primary-100 hover:text-primary-700"
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line" />
                发布讨论
              </button>
              <div className="mt-5 grid grid-cols-2 gap-3 border-t border-background-200 pt-5">
                <div>
                  <p className="font-heading text-xl font-bold text-foreground-950">1.2万</p>
                  <p className="text-xs text-foreground-500">今日讨论</p>
                </div>
                <div>
                  <p className="font-heading text-xl font-bold text-foreground-950">4,860</p>
                  <p className="text-xs text-foreground-500">活跃会员</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}